import { api } from '@/lib/api'
import { auth } from '@/lib/auth'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/auth/link')({
  component: AuthLink,
})

/**
 * 로그인 뒤 보낼 곳 (`?next=`).
 *
 * 구글 OAuth 심사자에게 `/setup/google` 처럼 **메뉴에 링크가 없는 화면**을 링크 하나로
 * 주려고 받는다. 메뉴에 두지 않은 건 의도다 — 미검증 scope 를 일반 고객사에게 노출하지
 * 않으려는 것이고(구글 안내: "숨겨진 테스트 경로에서만 새 범위를 트리거"), 그래서 이 링크가
 * 심사자의 유일한 입구다.
 *
 * ⚠️ **같은 사이트 안의 주소만 허용한다.** 그대로 따라가면 키가 담긴 신뢰된 링크가 외부
 * 사이트로 튕기는 오픈 리다이렉트가 된다. 문자열 검사(`startsWith('/')`)는 `//evil.com`·
 * `/\evil.com` 같은 우회를 놓치기 쉬워서, 브라우저가 실제로 해석한 origin 을 비교한다.
 * 해석이 안 되거나 다른 origin 이면 홈으로 보낸다.
 *
 * ⚠️ **검증한 절대 주소(`href`)를 그대로 돌려준다 — 경로만 떼어 돌려주면 안 된다.**
 * `https://<우리 도메인>//evil.com` 은 origin 검사를 통과하지만 pathname 이 `//evil.com` 이라,
 * 그걸 `location.href` 에 넣으면 브라우저가 프로토콜 상대 주소로 **다시 해석해** evil.com 으로
 * 간다(2026-09-21 브라우저 엔진 실측). 절대 주소는 재해석될 여지가 없다.
 */
function safeNext(raw: string | null): string {
  if (!raw) return '/'
  try {
    const url = new URL(raw, window.location.origin)
    if (url.origin !== window.location.origin) return '/'
    // `blob:https://<우리>/…` 는 origin 이 같게 나와 통과한다. http(s) 가 아닌 스킴은 닫는다.
    if (url.protocol !== window.location.protocol) return '/'
    return url.href
  } catch {
    return '/'
  }
}

function AuthLink() {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const authenticate = async () => {
      const params = new URLSearchParams(window.location.search)
      const key = params.get('key')

      if (!key) {
        setError('잘못된 접근입니다.')
        return
      }

      try {
        // 백엔드에 키 검증 요청
        const response = await api.get<{ access_token: string }>(
          '/v2/auth/link',
          { params: { key } },
        )

        // Meta 검수자용 시크릿 로그인(`v2/auth/router.py` GET /auth/link).
        // 어드민 임퍼소네이션이 아니다 — 그쪽은 `/?access_token=` 하나뿐이다.
        auth.setToken(response.data.access_token)
        const next = safeNext(params.get('next'))
        // ⚠️ 이동 전에 주소창에서 키를 지운다. 같은 origin 으로 이동하면 다음 문서의
        //    `document.referrer` 에 **전체 URL(= 키)** 이 실리고, Sentry 는 모든 이벤트에
        //    Referer 를 붙인다. next 로 작업 화면에 바로 가면 그 문서가 세션 내내 살아 있어
        //    에러 한 번에 키가 Sentry 에 저장된다. 뒤로가기 히스토리에서도 키가 빠진다
        //    (`__root.tsx` 의 `?token=` 처리와 같은 패턴).
        window.history.replaceState({}, document.title, window.location.pathname)
        window.location.href = next
      } catch {
        setError('인증에 실패했어요.')
      }
    }

    authenticate()
  }, [])

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center text-on-surface-variant">{error}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-outline-variant animate-spin" />
    </div>
  )
}
